import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, ArrowRight, Check, Phone } from "lucide-react";
import { Navbar, Footer, Reveal, ease } from "@/components/shared";
import { site, whatsappHref } from "@/config/site";

const CAT_MAP = {
  "organizzazione-funerali": "bare",
  "cremazione": "urne",
  "tumulazione": "lapidi",
  "trasporti": "carri",
  "pratiche-burocratiche": "pratiche",
  "allestimenti-floreali": "fiori",
};

export default function ServicePage() {
  const { slug } = useParams();
  const s = site.servizi.find((x) => x.slug === slug);
  const altri = site.servizi.filter((x) => x.slug !== slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!s) {
    return (
      <div>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 pt-header">
          <p className="text-muted-foreground">Servizio non trovato.</p>
          <Link to="/#servizi" className="text-primary border-b-2 border-accent">Torna ai servizi</Link>
        </div>
        <Footer />
      </div>
    );
  }
  const Icon = Icons[s.icon] || Icons.Circle;
  const cat = CAT_MAP[slug];

  return (
    <div data-testid="service-page">
      <Navbar />
      <section className="relative min-h-[46vh] flex items-end overflow-hidden pt-header">
        <motion.div className="absolute inset-0" initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease }}>
          <img src={s.foto} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/60 to-[#0A192F]/30" />
        </motion.div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-12 w-full">
          <Link to="/#servizi" className="inline-flex items-center gap-2 text-white/70 text-sm hover:text-white mb-4"><ArrowLeft size={16} /> Servizi</Link>
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent text-primary mb-4"><Icon size={22} /></span>
          <h1 className="text-white font-serif font-light text-5xl md:text-6xl">{s.titolo}</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-[1.4fr_0.6fr] gap-12 lg:gap-16">
        <div>
          <Reveal>
            <p className="text-xl md:text-2xl font-serif text-primary leading-relaxed">{s.dettaglio}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {s.punti.map((p) => (
                <div key={p} className="flex items-start gap-3 bg-secondary rounded-xl p-4">
                  <Check size={18} className="text-accent mt-0.5 shrink-0" />
                  <span className="text-primary">{p}</span>
                </div>
              ))}
            </div>
          </Reveal>
          {cat && (
            <Reveal delay={0.15}>
              <Link to={`/catalogo/${cat}`} className="mt-10 inline-flex items-center gap-2 text-primary font-semibold border-b-2 border-accent pb-1 hover:gap-3 transition-all">
                Vedi il catalogo correlato <ArrowRight size={18} />
              </Link>
            </Reveal>
          )}
        </div>

        <aside className="lg:sticky lg:top-36 h-fit">
          <div className="bg-primary text-primary-foreground rounded-2xl p-7 grain relative overflow-hidden">
            <div className="relative">
              <p className="overline mb-3">Hai bisogno di assistenza?</p>
              <p className="font-serif text-2xl leading-tight">Siamo disponibili 24 ore su 24</p>
              <a href={`tel:${site.telefonoRaw}`} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-accent text-primary font-semibold px-6 py-3 rounded-full">
                <Phone size={18} /> {site.telefono}
              </a>
              <a href={whatsappHref(`Buongiorno, vorrei informazioni sul servizio ${s.titolo}.`)} target="_blank" rel="noreferrer"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-white/30 px-6 py-3 rounded-full hover:bg-white/10 transition-all">
                WhatsApp
              </a>
              <Link to="/preventivo" className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-white/30 px-6 py-3 rounded-full hover:bg-white/10 transition-all">
                Crea il tuo preventivo <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <section className="bg-secondary py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline mb-6">Altri servizi</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {altri.map((a) => {
              const AIcon = Icons[a.icon] || Icons.Circle;
              return (
                <Link key={a.slug} to={`/servizi/${a.slug}`} data-testid={`altro-${a.slug}`}
                  className="group bg-card border border-border rounded-xl p-5 hover:-translate-y-1 hover:shadow-md transition-all">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent/15 text-accent"><AIcon size={18} /></span>
                  <p className="mt-4 font-serif text-lg text-primary leading-tight">{a.titolo}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
